import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Clock, MailCheck, MailX, Search, Edit2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_RSVP = {
  deadline: '2027-05-30',
  softNote: 'Please RSVP by',
  urgentNote: 'The RSVP window is closing — please RSVP by',
  closedNote: 'The RSVP window has closed. Please contact us directly if you still need to reply.',
};

const formatDeadline = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  const deadline = new Date(y, m - 1, d);
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((deadline - todayMidnight) / 86400000);
};

const RSVPDeadlineBanner = ({ rsvp }) => {
  if (!rsvp?.deadline) return null;
  const left = daysUntil(rsvp.deadline);
  const dateStr = formatDeadline(rsvp.deadline);
  if (left == null) return null;
  if (left < 0) {
    return (
      <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white/5 text-white/70 border border-white/15 text-xs" data-testid="rsvp-deadline-closed">
        <MailX className="w-3.5 h-3.5" />
        <span>{rsvp.closedNote || DEFAULT_RSVP.closedNote}</span>
      </div>
    );
  }
  const isUrgent = left <= 7;
  const isWarm = left <= 30;
  const tone = isUrgent ? 'urgent' : isWarm ? 'warm' : 'soft';
  const chip = isUrgent ? `Final week · ${left} day${left === 1 ? '' : 's'} left`
    : isWarm ? `Just ${left} day${left === 1 ? '' : 's'} left` : null;
  const noteText = isWarm ? (rsvp.urgentNote || DEFAULT_RSVP.urgentNote) : (rsvp.softNote || DEFAULT_RSVP.softNote);
  const wrapCls = { soft: 'text-white/85', warm: 'text-[#F5E7B5]', urgent: 'text-[#F9DA7C]' }[tone];
  const chipCls = {
    warm:   'bg-[#C8A951]/15 text-[#F5E7B5] border-[#C8A951]/45',
    urgent: 'bg-[#C8A951]/25 text-[#FFEDA6] border-[#C8A951]/70 shadow-[0_6px_28px_-14px_rgba(200,169,81,0.85)]',
  }[tone];
  return (
    <div className="mt-4 flex flex-col items-center gap-2" data-testid={`rsvp-deadline-banner-${tone}`} role="note" aria-live="polite">
      <p className={`${wrapCls} text-sm md:text-base`}>{noteText} <span className="font-serif italic">{dateStr}</span>.</p>
      {chip && (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] uppercase tracking-widest ${chipCls}`} data-testid={`rsvp-deadline-chip-${tone}`}>
          <Clock className="w-3 h-3" />{chip}
        </span>
      )}
    </div>
  );
};

/* ============================================================
   EverVow-aligned party-centric RSVP flow.

   1. Guest enters phone → /api/rsvp/lookup returns their party
      and any existing RSVP so the form pre-fills.
   2. Form renders the primary guest, any additional guests, and
      (if the party allows one AND the primary is attending) the
      plus-one field. Plus-one behavior matches EverVow exactly:
        • 'none'  → nothing shown
        • 'named' → pre-filled approved name, editable, meal fields
        • 'open'  → empty name field + meal fields
      There is deliberately NO "Will your plus-one attend?" prompt.
      A populated name is what marks the plus-one as attending.
   3. Submit upserts /api/rsvp/submit — retrieve/update never
      creates a duplicate; the same party can update as often as
      needed until the deadline.
   ============================================================ */

const emptyPersonResponse = () => ({ attending: 'yes', meal: '', dietary_restrictions: '' });

/**
 * EverVow-style dual-button attendance choice.
 *
 * Two full-width, side-by-side bordered buttons with uppercase tracked
 * labels — the active choice fills with the sable primary color, the
 * inactive stays parchment-white with a hairline border. Used for both
 * primary attendance ("Yes, Attending" / "Regretfully Decline") and the
 * plus-one ("Bringing +1" / "No +1").
 */
const AttendanceChoice = ({ value, options, onChange, disabled, testPrefix }) => (
  <div className="grid grid-cols-2 gap-3" data-testid={`${testPrefix}-attending`}>
    {options.map((opt) => {
      const active = value === opt.v;
      return (
        <button
          key={opt.v}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.v)}
          className={
            'w-full py-3 sm:py-3.5 rounded-md border text-[11px] sm:text-xs uppercase tracking-[0.2em] font-medium transition-colors ' +
            (active
              ? 'bg-[#3B2F2F] border-[#3B2F2F] text-white'
              : 'bg-white border-[#D8CFB9] text-[#3B2F2F] hover:border-[#C8A951] hover:bg-[#FBF7EE]') +
            (disabled ? ' opacity-50 cursor-not-allowed' : '')
          }
          data-testid={`${testPrefix}-attending-${opt.v}`}
        >
          {opt.l}
        </button>
      );
    })}
  </div>
);

/**
 * Per-guest response block (primary invitee + any additional named
 * guests). Layout adapted from the EverVow public-site preview:
 *   • Serif name heading on its own line
 *   • Two big side-by-side attendance buttons
 *   • Meal Choice + Dietary Restrictions revealed only when attending
 *
 * Kept as its own component so the exact same visual + interaction is
 * reused for every attendee in the party without drift.
 */
const PersonBlock = ({ label, response, onChange, disabled, testPrefix }) => {
  // Buffet-style catering — no per-guest meal choice. We only need to
  // know whether they have dietary restrictions / food allergies, and
  // if so what they are, so the couple can care for them at the buffet.
  const attending = response.attending === 'yes';
  const hasDietary = (response.has_dietary || (response.dietary_restrictions || '').trim() !== '') ? 'yes' : 'no';
  return (
    <div className="border-t border-[#E5E0D8] pt-6" data-testid={`${testPrefix}-block`}>
      <p className="font-serif text-2xl text-[#3B2F2F] mb-4" data-testid={`${testPrefix}-label`}>{label}</p>
      <AttendanceChoice
        value={response.attending}
        options={[
          { v: 'yes', l: 'Yes, Attending' },
          { v: 'no',  l: 'Regretfully Decline' },
        ]}
        onChange={(v) => onChange({ ...response, attending: v })}
        disabled={disabled}
        testPrefix={testPrefix}
      />
      {attending && (
        <div className="mt-5 space-y-4">
          <div>
            <p className="block text-[#3B2F2F] text-[11px] uppercase tracking-[0.18em] mb-2">
              Any Dietary Restrictions or Food Allergies?
            </p>
            <AttendanceChoice
              value={hasDietary}
              options={[{ v: 'yes', l: 'Yes' }, { v: 'no', l: 'No' }]}
              onChange={(v) => onChange({
                ...response,
                has_dietary: v === 'yes',
                dietary_restrictions: v === 'no' ? '' : (response.dietary_restrictions || ''),
              })}
              disabled={disabled}
              testPrefix={`${testPrefix}-dietary-choice`}
            />
          </div>
          {hasDietary === 'yes' && (
            <div>
              <label className="block text-[#3B2F2F] text-[11px] uppercase tracking-[0.18em] mb-2">
                Please let us know the specifics
              </label>
              <input
                type="text" value={response.dietary_restrictions || ''} disabled={disabled}
                onChange={(e) => onChange({ ...response, has_dietary: true, dietary_restrictions: e.target.value })}
                className="wedding-input" placeholder="e.g. gluten-free, nut allergy, shellfish…"
                data-testid={`${testPrefix}-dietary`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RSVPSection = () => {
  const [rsvp, setRsvp] = useState(DEFAULT_RSVP);
  const [name, setName] = useState('');
  const [secondaryPhone, setSecondaryPhone] = useState('');
  const [needsSecondary, setNeedsSecondary] = useState(false);
  const [lookupStatus, setLookupStatus] = useState({ type: '', message: '' });
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [party, setParty] = useState(null);
  const [existingRsvp, setExistingRsvp] = useState(null);
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/event/content`, { params: { _: Date.now() } });
        if (alive && res.data) setRsvp({ ...DEFAULT_RSVP, ...(res.data.rsvp || {}) });
      } catch { /* keep defaults */ }
    })();
    return () => { alive = false; };
  }, []);

  const handleLookup = async (e) => {
    e.preventDefault();
    setLookupStatus({ type: '', message: '' });
    setIsLookingUp(true);
    try {
      const body = { name };
      if (needsSecondary && secondaryPhone.trim()) body.phone = secondaryPhone.trim();
      const res = await axios.post(`${API}/rsvp/lookup`, body);
      if (res.data.needsSecondary) {
        setNeedsSecondary(true);
        setLookupStatus({
          type: 'info',
          message: 'We found more than one invitation with that name. Please enter the phone number associated with your invitation to continue.',
        });
        return;
      }
      const { party: p, rsvp: existing } = res.data;
      setParty(p);
      setExistingRsvp(existing);
      setForm(buildFormState(p, existing));
    } catch (err) {
      setLookupStatus({ type: 'error', message: err.response?.data?.detail || 'Lookup failed. Please try again.' });
    } finally {
      setIsLookingUp(false);
    }
  };

  const buildFormState = (p, existing) => {
    const additional = (p.additional_guests || []).map((_, idx) => {
      const found = existing?.additional_responses?.find((r) => r.guest_index === idx);
      return found ? { guest_index: idx, attending: found.attending, meal: found.meal || '', dietary_restrictions: found.dietary_restrictions || '' }
                   : { guest_index: idx, ...emptyPersonResponse() };
    });
    let plusOne = null;
    if (p.plus_one_type !== 'none') {
      const eP = existing?.plus_one;
      // Explicit "Bringing a guest? Yes/No" state — replaces the old
      // "leave-name-blank-means-declining" behavior so an invitee never
      // has to erase a pre-filled plus-one name to decline them.
      // Default when there's no prior response: named parties assume the
      // approved plus-one is coming ('yes'), open parties assume no
      // additional guest ('no') until the invitee opts in.
      let attending;
      if (existing) {
        attending = eP && eP.attending ? 'yes' : 'no';
      } else {
        attending = p.plus_one_type === 'named' ? 'yes' : 'no';
      }
      plusOne = {
        attending,
        // Named: default to the party's approved plus-one name. Open: keep
        // whatever the invitee typed last time (never erased on 'no').
        name: eP?.name ?? (p.plus_one_type === 'named' ? (p.plus_one_name || '') : ''),
        meal: eP?.meal || '',
        dietary_restrictions: eP?.dietary_restrictions || '',
      };
    }
    return {
      primary: {
        attending: existing?.primary_response?.attending || 'yes',
        meal: existing?.primary_response?.meal || '',
        dietary_restrictions: existing?.primary_response?.dietary_restrictions || '',
      },
      additional,
      plusOne,
      message: existing?.message || '',
      contact_email: existing?.contact?.email || p.primary_guest?.email || '',
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    setIsSubmitting(true);
    try {
      const payload = {
        party_id: party.id,
        primary_response: form.primary,
        additional_responses: form.additional,
        // Explicit plus-one attendance — only send it as "attending" when
        // the invitee said Yes AND a name is present. Saying No sends
        // `attending: false` so the backend excludes the plus-one from seat
        // counts and meal/dietary reports without the invitee needing to
        // erase the pre-filled name manually.
        plus_one: (party.plus_one_type !== 'none' && form.primary.attending === 'yes' && form.plusOne)
          ? (form.plusOne.attending === 'yes' && (form.plusOne.name || '').trim()
              ? { attending: true,  name: form.plusOne.name.trim(), meal: form.plusOne.meal || null, dietary_restrictions: form.plusOne.dietary_restrictions || null }
              : { attending: false, name: (form.plusOne.name || '').trim() || null, meal: null, dietary_restrictions: null })
          : null,
        message: form.message,
        contact_email: form.contact_email || null,
      };
      const res = await axios.post(`${API}/rsvp/submit`, payload);
      setExistingRsvp(res.data.rsvp);
      setStatus({
        type: 'success',
        message: res.data.isUpdate
          ? "Your RSVP has been updated. Thank you!"
          : "Thank you for your RSVP — we can't wait to celebrate with you!",
      });
    } catch (err) {
      setStatus({ type: 'error', message: err.response?.data?.detail || 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetLookup = () => {
    setParty(null); setExistingRsvp(null); setForm(null);
    setStatus({ type: '', message: '' });
    setName(''); setSecondaryPhone(''); setNeedsSecondary(false);
    setLookupStatus({ type: '', message: '' });
  };

  const mealOptions = party?.meal_options?.length ? party.meal_options : ['Chicken', 'Beef', 'Fish', 'Vegetarian', 'Vegan'];
  const primaryDeclined = form?.primary?.attending === 'no';
  const showPlusOne = !!form && party?.plus_one_type !== 'none' && !primaryDeclined;

  return (
    <section id="rsvp" className="section bg-[#3B2F2F]" data-testid="rsvp-section">
      <div className="max-w-3xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-10">
          <p className="font-cursive text-[#C8A951] text-2xl mb-2">Will You Join Us?</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">RSVP</h2>
          <div className="gold-divider" />
          <RSVPDeadlineBanner rsvp={rsvp} />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }} className="bg-white rounded-2xl shadow-2xl p-6 sm:p-10">
          {/* ---- Step 1: name lookup ---- */}
          {!party && (
            <form onSubmit={handleLookup} className="space-y-4" data-testid="rsvp-lookup-form">
              <div className="text-center mb-2">
                <h3 className="font-serif text-xl sm:text-2xl text-[#3B2F2F]">Look up your invitation</h3>
                <p className="text-sm text-[#5A4A4A] mt-1">Enter your name as it appears on your invitation to view or update your RSVP.</p>
              </div>
              {lookupStatus.type === 'error' && (
                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-600 text-sm" data-testid="rsvp-lookup-error">
                  <AlertCircle className="w-4 h-4 shrink-0" /><span>{lookupStatus.message}</span>
                </div>
              )}
              {lookupStatus.type === 'info' && (
                <div className="flex items-center gap-2 p-3 bg-[#FCFBF7] border border-[#E5E0D8] rounded-lg text-[#5A4A4A] text-sm" data-testid="rsvp-lookup-secondary-note">
                  <AlertCircle className="w-4 h-4 shrink-0" /><span>{lookupStatus.message}</span>
                </div>
              )}
              <div>
                <label className="block text-[#3B2F2F] text-sm font-medium mb-2">Full Name</label>
                <input
                  type="text" required value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="wedding-input" placeholder="First and Last Name"
                  autoComplete="name"
                  data-testid="rsvp-lookup-name"
                />
              </div>
              {needsSecondary && (
                <div>
                  <label className="block text-[#3B2F2F] text-sm font-medium mb-2">Phone number on your invitation</label>
                  <input
                    type="tel" required value={secondaryPhone}
                    onChange={(e) => setSecondaryPhone(e.target.value)}
                    className="wedding-input" placeholder="(555) 123-4567"
                    autoComplete="tel"
                    data-testid="rsvp-lookup-secondary-phone"
                  />
                </div>
              )}
              <button type="submit" disabled={isLookingUp} className="w-full btn-gold py-3 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="rsvp-lookup-submit">
                {isLookingUp ? 'Looking up…' : (<><Search className="w-4 h-4" /> Find my invitation</>)}
              </button>
            </form>
          )}

          {/* ---- Step 2: party form ---- */}
          {party && status.type !== 'success' && (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="rsvp-party-form">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#C8A951]">
                    {existingRsvp ? 'Updating your response' : 'Your invitation'}
                  </p>
                  <h3 className="font-serif text-xl text-[#3B2F2F]">
                    {party.party_name || `${party.primary_guest.first_name} ${party.primary_guest.last_name || ''}`.trim()}
                  </h3>
                </div>
                <button type="button" onClick={resetLookup} className="text-xs text-[#5A4A4A] underline hover:text-[#3B2F2F]" data-testid="rsvp-lookup-reset">Not you? Look up another</button>
              </div>

              {status.type === 'error' && (
                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-600 text-sm" data-testid="rsvp-submit-error">
                  <AlertCircle className="w-4 h-4 shrink-0" /><span>{status.message}</span>
                </div>
              )}

              <PersonBlock
                label={`${party.primary_guest.first_name} ${party.primary_guest.last_name || ''}`.trim()}
                response={form.primary}
                onChange={(v) => setForm((f) => ({ ...f, primary: v }))}
                mealOptions={mealOptions}
                testPrefix="rsvp-primary"
              />

              {form.additional.map((r, idx) => {
                const ag = party.additional_guests[idx];
                return (
                  <PersonBlock
                    key={idx}
                    label={`${ag.first_name} ${ag.last_name || ''}`.trim()}
                    response={{ attending: r.attending, meal: r.meal, dietary_restrictions: r.dietary_restrictions }}
                    onChange={(v) => setForm((f) => ({
                      ...f,
                      additional: f.additional.map((x, i) => i === idx ? { ...v, guest_index: idx } : x),
                    }))}
                    mealOptions={mealOptions}
                    disabled={primaryDeclined}
                    testPrefix={`rsvp-guest-${idx}`}
                  />
                );
              })}

              {showPlusOne && (
                <div className="border-t border-[#E5E0D8] pt-6" data-testid="rsvp-plus-one-block">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#8A7A6A] mb-3" data-testid="rsvp-plus-one-heading">
                    Plus-One{party.plus_one_type === 'named' && party.plus_one_name ? ` (${party.plus_one_name})` : ''}
                  </p>
                  <AttendanceChoice
                    value={form.plusOne.attending}
                    options={[
                      { v: 'yes', l: 'Bringing +1' },
                      { v: 'no',  l: 'No +1' },
                    ]}
                    onChange={(v) => setForm((f) => ({ ...f, plusOne: { ...f.plusOne, attending: v } }))}
                    testPrefix="rsvp-plus-one"
                  />

                  {form.plusOne.attending === 'yes' && (() => {
                    const hasDietary = (form.plusOne.has_dietary || (form.plusOne.dietary_restrictions || '').trim() !== '') ? 'yes' : 'no';
                    return (
                    <div className="mt-5 space-y-4">
                      <div>
                        <label className="block text-[#3B2F2F] text-[11px] uppercase tracking-[0.18em] mb-2">Guest Name</label>
                        <input
                          type="text" value={form.plusOne.name}
                          onChange={(e) => setForm((f) => ({ ...f, plusOne: { ...f.plusOne, name: e.target.value } }))}
                          className="wedding-input"
                          placeholder={party.plus_one_type === 'named' ? party.plus_one_name || 'Full name' : 'Full name'}
                          data-testid="rsvp-plus-one-name"
                        />
                      </div>
                      <div>
                        <p className="block text-[#3B2F2F] text-[11px] uppercase tracking-[0.18em] mb-2">
                          Any Dietary Restrictions or Food Allergies?
                        </p>
                        <AttendanceChoice
                          value={hasDietary}
                          options={[{ v: 'yes', l: 'Yes' }, { v: 'no', l: 'No' }]}
                          onChange={(v) => setForm((f) => ({
                            ...f,
                            plusOne: {
                              ...f.plusOne,
                              has_dietary: v === 'yes',
                              dietary_restrictions: v === 'no' ? '' : (f.plusOne.dietary_restrictions || ''),
                            },
                          }))}
                          testPrefix="rsvp-plus-one-dietary-choice"
                        />
                      </div>
                      {hasDietary === 'yes' && (
                        <div>
                          <label className="block text-[#3B2F2F] text-[11px] uppercase tracking-[0.18em] mb-2">
                            Please let us know the specifics
                          </label>
                          <input
                            type="text" value={form.plusOne.dietary_restrictions}
                            onChange={(e) => setForm((f) => ({ ...f, plusOne: { ...f.plusOne, has_dietary: true, dietary_restrictions: e.target.value } }))}
                            className="wedding-input" placeholder="e.g. gluten-free, nut allergy, shellfish…"
                            data-testid="rsvp-plus-one-dietary"
                          />
                        </div>
                      )}
                    </div>
                    );
                  })()}
                </div>
              )}

              <div className="border-t border-[#E5E0D8] pt-6 space-y-4">
                <div>
                  <label className="block text-[#3B2F2F] text-[11px] uppercase tracking-[0.18em] mb-2">
                    Contact Email <span className="normal-case tracking-normal text-[#8A7A7A]">(optional)</span>
                  </label>
                  <input
                    type="email" value={form.contact_email}
                    onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                    className="wedding-input" placeholder="your@email.com"
                    data-testid="rsvp-contact-email"
                  />
                </div>

                <div>
                  <label className="block text-[#3B2F2F] text-[11px] uppercase tracking-[0.18em] mb-2">
                    A Note for the Couple <span className="normal-case tracking-normal text-[#8A7A7A]">(optional)</span>
                  </label>
                  <textarea
                    rows="3" value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    className="wedding-input resize-none"
                    placeholder="Share your excitement, blessings, or a note…"
                    data-testid="rsvp-message"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-gold py-4 text-sm sm:text-base uppercase tracking-[0.28em] font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="rsvp-submit-button"
              >
                {isSubmitting
                  ? 'Sending…'
                  : (<><Send className="w-4 h-4" />{existingRsvp ? 'Update RSVP' : 'Submit RSVP'}</>)}
              </button>
            </form>
          )}

          {/* ---- Step 3: confirmation ---- */}
          {status.type === 'success' && (
            <div className="text-center py-8" data-testid="rsvp-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="font-serif text-2xl text-[#3B2F2F] mb-2">Thank You!</h3>
              <p className="text-[#5A4A4A]">{status.message}</p>
              <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => { setStatus({ type: '', message: '' }); setForm(buildFormState(party, existingRsvp)); }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E0D8] rounded-full text-sm text-[#3B2F2F] hover:bg-[#F8F5EF]"
                  data-testid="rsvp-edit-again"
                >
                  <Edit2 className="w-4 h-4" /> Edit my response
                </button>
                <button
                  type="button"
                  onClick={resetLookup}
                  className="text-sm text-[#5A4A4A] underline hover:text-[#3B2F2F]"
                  data-testid="rsvp-new-lookup"
                >
                  Look up another invitation
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export { RSVPSection };
